import { useMemo } from "react";
import { Head } from "../components/layout/Head.js";
import { MainColumn } from "../components/layout/MainColumn.js";
// Vite ?raw import: file content is inlined at build time.
// CREDITS.md is the canonical artifact, auto-generated by
// pipeline/scripts/generate-credits.ts.
import creditsMd from "../../../CREDITS.md?raw";

interface Contributor {
  name: string;
  resources: number;
  topics: number;
}

// Render text containing **bold** spans as React nodes. Splits on the
// markdown bold pair and emits <strong> for the matched fragments. Keeps
// `<code>` spans (backtick pairs) intact too because the auto-generated
// intro contains `content/**/*.md` / `pipeline/scripts/generate-credits.ts`
// path references that read better as inline code.
function renderInlineMarkdown(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  // Tokenise on either **bold** or `code`, preserving the surrounding text.
  const re = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let lastIndex = 0;
  let key = 0;
  for (const m of text.matchAll(re)) {
    if (m.index === undefined) continue;
    if (m.index > lastIndex) parts.push(text.slice(lastIndex, m.index));
    const token = m[0];
    if (token.startsWith("**")) {
      parts.push(<strong key={key++} className="text-text">{token.slice(2, -2)}</strong>);
    } else {
      parts.push(<code key={key++} className="font-mono text-[12.5px] bg-panel-2 px-xs rounded-sm">{token.slice(1, -1)}</code>);
    }
    lastIndex = m.index + token.length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
}

// Parse the auto-generated CREDITS.md. Each contributor row looks like:
//   - **Name** — 3 resources across 2 topics
// (em-dash, with the numeric suffix). We split the file at the "---"
// separator and read everything after it as the contributor list.
function parseCredits(md: string): { header: string; intro: string; contributors: Contributor[] } {
  const [headerBlock = "", body = ""] = md.split(/\n---\n/);
  const headerLines = headerBlock.split("\n");
  const header = headerLines[0]?.replace(/^#\s+/, "") ?? "Credits";
  const intro = headerLines.slice(1).join("\n").trim();

  const contributors: Contributor[] = [];
  const rowRe = /^-\s+\*\*(.+?)\*\*\s+—\s+(\d+)\s+resources?\s+across\s+(\d+)\s+topics?\s*$/;
  for (const raw of body.split("\n")) {
    const m = rowRe.exec(raw.trim());
    if (!m || !m[1] || !m[2] || !m[3]) continue;
    contributors.push({ name: m[1], resources: Number(m[2]), topics: Number(m[3]) });
  }
  return { header, intro, contributors };
}

export function Credits() {
  const { header, intro, contributors } = useMemo(() => parseCredits(creditsMd), []);

  return (
    <div className="p-xl">
      <Head title={header} />
      <MainColumn maxWidth="max-w-[720px]">
        <h1 className="text-display-xl m-0 mb-md">{header}</h1>
        {intro && (
          <div className="text-body text-text-mute leading-[1.6] mb-xl">
            {intro.split(/\n\n+/).map((para, i) => (
              <p key={i} className="m-0 mb-md last:mb-0">{renderInlineMarkdown(para)}</p>
            ))}
          </div>
        )}
        <ul className="flex flex-col gap-sm">
          {contributors.map(c => (
            <li key={c.name} className="flex items-baseline justify-between gap-md py-xs border-b border-border-soft">
              <span className="text-body-strong text-text">{c.name}</span>
              <span className="text-caption text-text-dim tabular-nums shrink-0">
                {c.resources} {c.resources === 1 ? "resource" : "resources"} · {c.topics} {c.topics === 1 ? "topic" : "topics"}
              </span>
            </li>
          ))}
        </ul>
      </MainColumn>
    </div>
  );
}
